import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * "Completa il tuo ordine": carosello (o griglia) di prodotti nel cart drawer (snippets/cart-upsell.liquid).
 *
 * - All'apertura del drawer chiede alla section tecnica `cart-upsell` l'HTML via Section Rendering API
 *   (GET /search?section_id=cart-upsell[&q=id:A OR id:B&resources[type]=product]): una richiesta per apertura,
 *   asincrona, il drawer e il checkout non la aspettano. I prodotti casuali cambiano tra un'apertura e l'altra
 *   (seme a 30 secondi lato server).
 * - Il markup del server (ogni morph del drawer) contiene solo il contenitore vuoto: l'HTML ricevuto viene tenuto in
 *   memoria e reinserito appena il morph lo cancella (MutationObserver sul contenitore), ripristinando la posizione
 *   di scorrimento del carosello.
 * - Dopo ogni aggiornamento del carrello le card dei prodotti ora nel carrello vengono nascoste (`data-in-cart`,
 *   reso da Liquid e aggiornato dal morph); se restano meno di 2 prodotti visibili si richiede di nuovo la section.
 * - Le pill varianti (`on:click="cart-upsell-component/addVariant"`) impostano la variante nel form del prodotto e
 *   lo inviano: da lì l'aggiunta è gestita da product-form.js e il drawer resta aperto.
 */
class CartUpsellComponent extends Component {
  /** @type {string | null} Ultimo HTML ricevuto dalla section; `null` finché non è stato chiesto nulla */
  #html = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Element | null} */
  #drawer = null;

  /** @type {string | null} Stato del carrello (`data-in-cart`) per cui è già stata rifatta la richiesta */
  #reloadedFor = null;

  /** @type {number} Posizione di scorrimento del carosello, da ripristinare dopo un reinserimento */
  #scrollLeft = 0;

  #observer = new MutationObserver(() => this.#scheduleRender());

  connectedCallback() {
    super.connectedCallback();

    this.#drawer = this.closest('cart-drawer-component');
    // `dialog:open` non propaga: va ascoltato sul componente del drawer che lo emette
    this.#drawer?.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    // `scroll` non propaga: lo intercettiamo in fase di cattura per ricordare la posizione del carosello
    this.addEventListener('scroll', this.#onScroll, { capture: true, passive: true });
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'data-in-cart'],
    });

    // Il componente può nascere a drawer già aperto (es. dopo lo svuotamento del carrello e una nuova aggiunta)
    if (this.#isDrawerOpen()) this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#drawer?.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.removeEventListener('scroll', this.#onScroll, { capture: true });
    this.#observer.disconnect();
    this.#abortController?.abort();
  }

  /** Invocata da assets/morph.js dopo un re-render via Section Rendering API: il contenitore torna vuoto. */
  updatedCallback() {
    super.updatedCallback();
    this.#scheduleRender();
  }

  /**
   * Chiamata da on:click="cart-upsell-component/addVariant" sulle pill varianti: scrive la variante (e la quantità
   * minima) nel form del prodotto e lo invia con requestSubmit, così il `submit` raggiunge `on:submit="/handleSubmit"`
   * del product-form-component del tema (aggiunta via AJAX con le section del drawer, poi morph).
   * @param {Event} event
   */
  addVariant(event) {
    const button = event.target instanceof Element ? event.target.closest('button[data-variant-id]') : null;
    const form = button?.closest('product-form-component')?.querySelector('form');
    const variantInput = form?.querySelector('input[name="id"]');
    const quantityInput = form?.querySelector('input[name="quantity"]');

    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    if (!(form instanceof HTMLFormElement) || !(variantInput instanceof HTMLInputElement)) return;

    variantInput.value = button.dataset.variantId ?? '';
    if (quantityInput instanceof HTMLInputElement && button.dataset.quantityMin) {
      quantityInput.value = button.dataset.quantityMin;
    }

    // Protezione dal doppio click: la pill resta disabilitata finché il morph del drawer non ricrea il carosello
    button.disabled = true;
    button.setAttribute('data-adding', '');
    form.requestSubmit();
  }

  #onDialogOpen = () => {
    this.#load();
  };

  /** Il morph del drawer avviene prima dei microtask: al render `data-in-cart` è già quello nuovo. */
  #onCartUpdate = () => {
    this.#scheduleRender();
  };

  /** @param {Event} event */
  #onScroll = (event) => {
    const scroller = event.target;
    if (scroller instanceof HTMLElement && scroller.tagName === 'SLIDESHOW-SLIDES') {
      this.#scrollLeft = scroller.scrollLeft;
    }
  };

  /** @returns {HTMLElement | null} */
  get #content() {
    const content = this.querySelector(':scope > .cart-upsell__content');
    return content instanceof HTMLElement ? content : null;
  }

  #isDrawerOpen() {
    const dialog = this.#drawer?.querySelector('dialog');
    return dialog instanceof HTMLDialogElement && dialog.open;
  }

  /** @returns {Set<string>} id dei prodotti nel carrello, da `data-in-cart` */
  #inCartIds() {
    return new Set((this.dataset.inCart ?? '').split(',').filter(Boolean));
  }

  /**
   * Costruisce l'URL della section; `null` quando non c'è nulla da chiedere (solo visti di recente, storico vuoto).
   * @returns {URL | null}
   */
  #buildUrl() {
    const { source = 'recently_viewed_random', sectionId = 'cart-upsell' } = this.dataset;
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('section_id', sectionId);

    if (source === 'random') return url;

    const inCart = this.#inCartIds();
    const ids = RecentlyViewed.getProducts().filter((/** @type {string} */ id) => !inCart.has(String(id)));

    if (ids.length === 0) return source === 'recently_viewed' ? null : url;

    url.searchParams.set('q', ids.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    return url;
  }

  async #load() {
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;

    const url = this.#buildUrl();
    if (!url) {
      this.#html = '';
      this.#scheduleRender();
      return;
    }

    try {
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = doc.querySelector('[data-cart-upsell-items]');
      // Gli stili `{% stylesheet %}` della section arrivano in un <style data-section-stylesheet>: li portiamo con noi
      const stylesheet = doc.querySelector('style[data-section-stylesheet]');

      this.#html = items?.innerHTML.trim() ? `${stylesheet?.outerHTML ?? ''}${items.innerHTML}` : '';
      // Contenuto nuovo: si riparte dall'inizio del carosello
      this.#scrollLeft = 0;
      this.#content?.replaceChildren();
      this.#scheduleRender();
    } catch (error) {
      if (controller.signal.aborted) return;
      // Nell'editor tema la section può non essere disponibile alla Section Rendering API: nessun rumore in console
      if (!Shopify.designMode) console.error('cart-upsell: caricamento fallito', error);
    }
  }

  #scheduleRender() {
    queueMicrotask(() => this.#render());
  }

  /** Inserisce (o reinserisce dopo un morph) l'HTML ricevuto e nasconde i prodotti ora nel carrello. Idempotente. */
  #render() {
    const content = this.#content;
    if (this.#html === null || !content) return;

    if (!this.#html) {
      content.replaceChildren();
      this.hidden = true;
      return;
    }

    if (!content.firstElementChild) {
      content.innerHTML = this.#html;
      this.#restoreScroll(content);
    }

    const inCart = this.#inCartIds();
    let visible = 0;
    let hiddenCount = 0;

    for (const card of content.querySelectorAll('.cart-upsell__card[data-product-id]')) {
      const item = card.closest('slideshow-slide, .cart-upsell__item');
      if (!(card instanceof HTMLElement) || !(item instanceof HTMLElement)) continue;

      const nowInCart = inCart.has(card.dataset.productId ?? '');
      if (item.hidden !== nowInCart) item.hidden = nowInCart;
      nowInCart ? hiddenCount++ : visible++;
    }

    if (this.hidden !== (visible === 0)) this.hidden = visible === 0;

    // Restano pochi prodotti: nuova richiesta (nuove esclusioni e nuovi casuali), una sola volta per stato del carrello
    const cartKey = this.dataset.inCart ?? '';
    if (hiddenCount > 0 && visible < 2 && this.#isDrawerOpen() && this.#reloadedFor !== cartKey) {
      this.#reloadedFor = cartKey;
      this.#load();
    }
  }

  /**
   * Dopo un reinserimento lo slideshow si inizializza sulla prima slide: riportiamo lo scorrimento dov'era,
   * senza animazione (lo slideshow aggiorna da solo la slide corrente ascoltando lo scroll).
   * @param {HTMLElement} content
   */
  #restoreScroll(content) {
    const scrollLeft = this.#scrollLeft;
    if (!scrollLeft) return;

    const scroller = content.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      const behavior = scroller.style.scrollBehavior;
      scroller.style.scrollBehavior = 'auto';
      scroller.scrollLeft = scrollLeft;
      scroller.style.scrollBehavior = behavior;
    });
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
